import { TAG_TYPES } from './rtk/constants';
import type * as Types from './rtk/rtk.generated';
import type { HighlighterPoints } from './rtk/rtk.generated';
import { highlighterApi as _highlighterApi } from './rtk/rtk.generated';

export * from './rtk/constants';
export type { Types as HighlighterApi };
export * from './types';
export * from './constants';
export * from './mocks';

// Alias named type import
export type {
  ActivateAccountResponse,
  ActivateHighlighterResponseRead,
  OnboardingStateResponse,
  OnboardingStateResponseRead,
  PostMyHighlighterAccountsAcceptTosApiResponse,
} from './rtk/rtk.generated';
export type {
  FunnelQuestionRow,
  Highlighter,
  HighlighterAddress,
  HighlighterPoints,
  HighlighterStudy,
  HighlighterSurveyRead,
  PointTransaction,
  MaterializedSurveyDto,
  Survey,
  HlSurveyType,
  SjsPlainData,
  SurveyInstance,
  HighlighterGenerationJobStatus,
  StartGenerationResponse,
} from './rtk/rtk.generated';

import type { components } from './fetch/fetch.generated';

export type CreateGuestHighlighterRequest = components['schemas']['CreateGuestHighlighterRequest'];

const enhancedHighlighterApi = _highlighterApi.enhanceEndpoints({
  endpoints: {
    // Highlighter Account enhancer (inlined)
    postMyHighlighterAddresses: {
      invalidatesTags: () => [TAG_TYPES.HIGHLIGHTER],
    },
    putMyHighlighterAddresses: {
      invalidatesTags: () => [TAG_TYPES.HIGHLIGHTER],
    },
    putAdminHighlightersByHighlighterIdAddress: {
      invalidatesTags: () => ['HighlighterAddresses', 'Highlighters'],
    },
    postAdminHighlightersByHighlighterIdAddress: {
      invalidatesTags: () => ['HighlighterAddresses', 'Highlighters'],
    },
    postHighlighterSurveysDecline: {
      invalidatesTags: () => [TAG_TYPES.HIGHLIGHTER_ANSWER, TAG_TYPES.HIGHLIGHTER],
    },

    postMyBoxesReceive: {
      // refresh studies after box created and survey dates set + sent
      invalidatesTags: () => ['HighlighterStudies'],
    },

    // Highlighter Activation enhancer (inlined)
    postMyHighlighterAccountsActivateHighlighter: {
      invalidatesTags: () => [TAG_TYPES.HL_SURVEY],
    },
    // Highlighter Answers enhancer (inlined)
    getMyHighlighterAnswersSurveysBySurveyInstanceIdAnswers: {
      providesTags: (_result, _error, arg) => [
        { type: TAG_TYPES.HIGHLIGHTER_ANSWER, id: arg.surveyInstanceId },
      ],
    },

    // Referral Codes enhancer (inlined)
    getAdminReferralCodes: {
      providesTags: (referralCodes) =>
        referralCodes
          ? [
              ...referralCodes.map(({ id }) => ({
                type: TAG_TYPES.REFERRAL_CODE,
                id: id,
              })),
              TAG_TYPES.REFERRAL_CODE,
            ]
          : [TAG_TYPES.REFERRAL_CODE],
    },
    getAdminReferralCodesById: {
      providesTags: (code) => [{ type: TAG_TYPES.REFERRAL_CODE, id: code?.id }],
    },
    // Survey Instances enhancer (inlined)
    getAdminSurveysBySurveyIdSurveyInstances: {
      providesTags: (surveyInstances) =>
        surveyInstances
          ? [
              ...surveyInstances.map(({ id }) => ({
                type: 'SurveyInstances' as const,
                id: id,
              })),
              'SurveyInstances',
            ]
          : ['SurveyInstances'],
    },
    postAdminSurveysBySurveyIdSenditemsurvey: {
      invalidatesTags: ['SurveyInstances'],
    },

    // Surveys enhancer (inlined)
    getAdminProjectsByIdSurveys: {
      providesTags: (surveys) =>
        surveys
          ? [
              ...surveys.map(({ id }) => ({
                type: TAG_TYPES.HL_SURVEY,
                id: id ?? undefined,
              })),
              TAG_TYPES.HL_SURVEY,
            ]
          : [TAG_TYPES.HL_SURVEY],
    },
    getAdminSurveys: {
      providesTags: (surveys) =>
        surveys
          ? [
              ...surveys.map(({ id }) => ({
                type: TAG_TYPES.HL_SURVEY,
                id: id ?? undefined,
              })),
              TAG_TYPES.HL_SURVEY,
            ]
          : [TAG_TYPES.HL_SURVEY],
    },
    getAdminSurveysById: {
      providesTags: (survey) =>
        survey
          ? [
              {
                type: TAG_TYPES.HL_SURVEY,
                id: survey.id ?? undefined,
              },
              TAG_TYPES.HL_SURVEY,
            ]
          : [TAG_TYPES.HL_SURVEY],
    },
    getMyProfileSurveys: {
      providesTags: (profileSurveys) =>
        profileSurveys
          ? [
              ...profileSurveys.map(({ highlighterSurvey }) => ({
                type: 'HighlighterSurveys' as const,
                id: highlighterSurvey?.id ?? undefined,
              })),
              'HighlighterSurveys' as const,
            ]
          : ['HighlighterSurveys' as const],
    },
    patchMyHighlighterSurveysBySurveyInstanceId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: TAG_TYPES.HL_SURVEY, id: arg?.surveyInstanceId },
        { type: TAG_TYPES.HIGHLIGHTER_ANSWER, id: arg?.surveyInstanceId },
        'HighlighterStudies',
      ],
    },
    putAdminSurveysById: {
      invalidatesTags: [TAG_TYPES.HL_SURVEY, TAG_TYPES.SURVEY_INSTANCE],
    },
    postAdminSurveys: {
      invalidatesTags: [TAG_TYPES.HL_SURVEY],
    },
    postAdminSurveysBySurveyIdFailedQc: {
      invalidatesTags: [TAG_TYPES.SURVEY_INSTANCE],
    },
    postAdminSurveysByProfileSurveyIdSendprofilesurvey: {
      invalidatesTags: [TAG_TYPES.SURVEY_INSTANCE],
    },
    getAdminSurveysBySurveyIdPreviewNudge: {
      providesTags: [TAG_TYPES.HL_SURVEY],
    },
    putAdminSurveysBySurveyIdActions: {
      invalidatesTags: [TAG_TYPES.SURVEY_INSTANCE],
    },

    postAdminPointTransactionGetRewardsCsv: {
      query: () => ({
        url: `/admin/PointTransaction/$getRewardsCsv`,
        method: 'POST',
        responseHandler: async (response) => response.text(),
      }),
    },
  },
});

/**
 * highlighter-app–only: `pages/api/my/points` reads from DB. Not a HighlighterApi
 * route anymore; do not call this hook from CSM/client (their `/api` proxy would 404).
 * Injected so OpenAPI regen keeps `useGetMyPointsQuery` for rewards.
 */
const highlighterApiWithLocalEndpoints = enhancedHighlighterApi.injectEndpoints({
  endpoints: (build) => ({
    getMyPoints: build.query<HighlighterPoints, void>({
      query: () => ({ url: `/my/points` }),
      // Same tag as admin PointTransaction endpoints.
      providesTags: ['PointTransaction'],
    }),
  }),
});

type DeprecatedMethods = 'usePostMyHighlighterAccounts';
export const highlighterApi: Omit<typeof highlighterApiWithLocalEndpoints, DeprecatedMethods> =
  highlighterApiWithLocalEndpoints;

/**
 *  @deprecated use exports from {@link highlighterApi} directly instead
 */
export const {
  useGetMyHighlighterAnswersSurveysBySurveyInstanceIdAnswersQuery: useGetHighlighterSurveyAnswers,
  useGetAdminClientsQuery: useGetProjectsQuery,
  useLazyGetAdminClientsQuery: useLazyGetProjectsQuery,
  useGetAdminHighlighterQuestionsQuery,
  useLazyGetAdminHighlighterQuestionsQuery,
  useGetAdminReferralCodesQuery: useGetReferralCodesQuery,
  useGetAdminReferralCodesByIdQuery: useGetReferralCodeByIdQuery,
  usePostAdminReferralCodesMutation: useCreateReferralCodeMutation,
  usePutAdminReferralCodesByReferralCodeIdMutation: useUpdateReferralCodeMutation,

  useGetAdminSurveysBySurveyIdSurveyInstancesQuery: useGetSurveyInstancesBySurveyIdQuery,
  useLazyGetAdminSurveysBySurveyIdSurveyInstancesQuery: useLazyGetSurveyInstancesBySurveyIdQuery,
  usePatchAdminSurveyInstancesBySurveyInstanceIdMutation: useUpdateSurveyInstanceStatusMutation,
  usePostAdminSurveysBySurveyIdSurveyInstancesMutation: useAddSurveyInstancesBySurveyIdMutation,
  usePutAdminSurveysBySurveyIdActionsMutation: useSurveyInstanceActionMutation,

  useGetAdminProjectsByIdSurveysQuery: useGetProjectSurveysQuery,
  useLazyGetAdminProjectsByIdSurveysQuery: useLazyGetProjectSurveysQuery,
  useGetAdminSurveysQuery: useGetSurveysQuery,
  useLazyGetAdminSurveysQuery: useLazyGetSurveysQuery,
  useGetAdminSurveysByIdQuery: getSurveyByIdQuery,
  useLazyGetAdminSurveysByIdQuery: getLazySurveyByIdQuery,
  usePatchMyHighlighterSurveysBySurveyInstanceIdMutation: usePatchMyHighlighterSurveyMutation,
  usePostAdminSurveysMutation: useCreateSurveyMutation,
  usePostAdminSurveysBySurveyIdFailedQcMutation: useFailedQCMutation,
  usePutAdminSurveysByIdMutation: useUpdateSurveyMutation,
  usePostAdminSurveysByProfileSurveyIdSendprofilesurveyMutation: useSendProfileSurveyMutation,
  useGetAdminSurveysBySurveyIdPreviewNudgeQuery: usePreviewNudgeEmail,

  useGetMyHighlighterAccountsQuery,
  useLazyGetMyHighlighterAccountsQuery,
  useGetMyHighlighterAnswersSurveysBySurveyInstanceIdAnswersQuery,
  useLazyGetMyHighlighterAnswersSurveysBySurveyInstanceIdAnswersQuery,
  usePostMyHighlighterAddressesMutation,
  usePostMyHighlighterAccountsBirthdateMutation,
  usePutMyHighlighterAddressesMutation,
  usePutMyHighlighterAccountsMutation,
  usePostHighlighterAccountsSubscriptionSettingsByHighlighterIdMutation:
    usePostHighlighterAccountSubscriptionSettings,
  usePostHighlighterSurveysDeclineMutation,
  useGetHighlighterAccountsSubscriptionSettingsByHighlighterIdQuery:
    useGetHighlighlighterAccountSubscriptionSettings,
  usePostMyBoxesReceiveMutation,
} = highlighterApi;
